/**
 * SlotSchedulingSystem.js - Core slot scheduling logic for Simulation Mode
 *
 * Handles:
 * - Slot time generation with realistic random minutes
 * - Slot assignment and validation
 * - Warning system (15-min, 5-min)
 * - Delay penalty calculations
 * - Cascade detection
 */

import { AIRPORTS } from '../data/airports.js';

// ============================================================================
// CONSTANTS
// ============================================================================

// Realistic minute values for departure times (not on quarter hours)
const REALISTIC_MINUTES = [3, 7, 13, 17, 23, 26, 34, 38, 41, 47, 53, 57];

// Hub slot times (hours) - spread throughout the day
const HUB_SLOT_HOURS = [6, 6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 17, 18, 19];

// Regional airport slot hours - morning, midday, evening
const REGIONAL_SLOT_HOURS = [
  [7, 12, 17],  // KRDM pattern
  [8, 14, 18],  // KSEA pattern
  [9, 13, 17],  // KBOI pattern
];

// Slot status values
export const SLOT_STATUS = {
  AVAILABLE: 'available',
  ASSIGNED: 'assigned',
  WARNING: 'warning',     // 15 min before
  URGENT: 'urgent',       // 5 min before
  BOARDING: 'boarding',   // Preflight complete, boarding in progress
  DELAYED: 'delayed',     // Missed departure time
  DEPARTED: 'departed',   // Flight has taken off
  COMPLETED: 'completed', // Flight completed for the day
};

// ============================================================================
// SLOT TIME GENERATION
// ============================================================================

/**
 * Generate a random realistic minute value
 */
function randomMinute() {
  return REALISTIC_MINUTES[Math.floor(Math.random() * REALISTIC_MINUTES.length)];
}

/**
 * Format time as HH:MM string
 */
export function formatSlotTime(hours, minutes) {
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Parse HH:MM string to { hours, minutes }
 */
export function parseSlotTime(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') {
    return { hours: 0, minutes: 0 };
  }
  const parts = timeStr.split(':');
  if (parts.length < 2) {
    return { hours: 0, minutes: 0 };
  }
  const [hours, minutes] = parts.map(Number);
  return { hours: hours || 0, minutes: minutes || 0 };
}

/**
 * Convert HH:MM to minutes since midnight
 */
export function slotTimeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const { hours, minutes } = parseSlotTime(timeStr);
  return hours * 60 + minutes;
}

/**
 * Format time for display (e.g., "6:13 AM")
 */
export function formatTimeDisplay(timeStr) {
  if (!timeStr) return '--:--';
  const { hours, minutes } = parseSlotTime(timeStr);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Generate hub slots with realistic random times
 * @param {string} hubCode - Airport code (e.g., "KPDX")
 * @returns {Array} Array of slot objects
 */
export function generateHubSlots(hubCode) {
  const slots = [];

  // Generate 14 slots spread throughout the day
  HUB_SLOT_HOURS.forEach((hour, index) => {
    const minute = randomMinute();
    slots.push({
      slotId: `${hubCode}-${(index + 1).toString().padStart(2, '0')}`,
      departureTime: formatSlotTime(hour, minute),
      monthlyFee: 0,
      expiresAt: null,
      isHubSlot: true,
      // Assignment tracking (daily reset)
      assignedFlightId: null,
      assignedAircraftId: null,
      assignedCrewId: null,
      assignedDestination: null,
      assignmentDate: null,
      status: SLOT_STATUS.AVAILABLE,
    });
  });

  // Sort by departure time
  slots.sort((a, b) => slotTimeToMinutes(a.departureTime) - slotTimeToMinutes(b.departureTime));

  return slots;
}

/**
 * Generate regional airport slots (3 slots each)
 * @param {string} airportCode - Airport code
 * @param {number} patternIndex - Which time pattern to use (0, 1, or 2)
 * @param {number} monthlyFee - Monthly gate lease fee
 * @returns {Array} Array of slot objects
 */
export function generateRegionalSlots(airportCode, patternIndex, monthlyFee) {
  const hours = REGIONAL_SLOT_HOURS[patternIndex % REGIONAL_SLOT_HOURS.length];

  return hours.map((hour, index) => ({
    slotId: `${airportCode}-${(index + 1).toString().padStart(2, '0')}`,
    departureTime: formatSlotTime(hour, randomMinute()),
    monthlyFee,
    expiresAt: null,
    isStarterSlot: true,
    // Assignment tracking
    assignedFlightId: null,
    assignedAircraftId: null,
    assignedCrewId: null,
    assignedDestination: null,
    assignmentDate: null,
    status: SLOT_STATUS.AVAILABLE,
  }));
}

/**
 * Generate all starting slots for Simulation Mode
 * Called once at game initialization
 */
export function generateSimulationSlots() {
  return {
    KPDX: generateHubSlots('KPDX'),
    KRDM: generateRegionalSlots('KRDM', 0, 5000),
    KSEA: generateRegionalSlots('KSEA', 1, 15000),
    KBOI: generateRegionalSlots('KBOI', 2, 10000),
  };
}

// ============================================================================
// SLOT ASSIGNMENT
// ============================================================================

/**
 * Assign aircraft, crew, and destination to a slot
 * @returns {{ success: boolean, error?: string, slot?: object }}
 */
export function assignSlot(slots, slotId, aircraftId, crewId, destination, gameDay) {
  // Find the slot
  let targetSlot = null;
  let airportCode = null;

  for (const [code, airportSlots] of Object.entries(slots)) {
    const found = airportSlots.find(s => s.slotId === slotId);
    if (found) {
      targetSlot = found;
      airportCode = code;
      break;
    }
  }

  if (!targetSlot) {
    return { success: false, error: 'Slot not found' };
  }

  if (targetSlot.status !== SLOT_STATUS.AVAILABLE) {
    return { success: false, error: 'Slot already assigned' };
  }

  // Create updated slot
  const updatedSlot = {
    ...targetSlot,
    assignedAircraftId: aircraftId,
    assignedCrewId: crewId,
    assignedDestination: destination,
    assignmentDate: gameDay,
    status: SLOT_STATUS.ASSIGNED,
  };

  return { success: true, slot: updatedSlot, airportCode };
}

/**
 * Clear a slot assignment
 */
export function clearSlotAssignment(slots, slotId) {
  for (const [code, airportSlots] of Object.entries(slots)) {
    const index = airportSlots.findIndex(s => s.slotId === slotId);
    if (index !== -1) {
      return {
        success: true,
        airportCode: code,
        slot: {
          ...airportSlots[index],
          assignedFlightId: null,
          assignedAircraftId: null,
          assignedCrewId: null,
          assignedDestination: null,
          assignmentDate: null,
          status: SLOT_STATUS.AVAILABLE,
        },
      };
    }
  }
  return { success: false, error: 'Slot not found' };
}

/**
 * Reset all slot assignments for a new day
 */
export function resetDailySlotAssignments(slots) {
  const resetSlots = {};

  for (const [code, airportSlots] of Object.entries(slots)) {
    resetSlots[code] = airportSlots.map(slot => ({
      ...slot,
      assignedFlightId: null,
      assignedAircraftId: null,
      assignedCrewId: null,
      assignedDestination: null,
      assignmentDate: null,
      status: SLOT_STATUS.AVAILABLE,
    }));
  }

  return resetSlots;
}

// ============================================================================
// WARNING SYSTEM
// ============================================================================

/**
 * Check slots for warning state changes
 * Called from GameLoopEngine on each tick
 * @returns {{ updates: Array, warnings: Array }} Updates to apply and warning messages
 */
export function processSlotWarnings(slots, flights, gameTime) {
  const updates = [];
  const warnings = [];

  const currentHours = gameTime.getHours();
  const currentMinutes = gameTime.getMinutes();
  const currentTotalMinutes = currentHours * 60 + currentMinutes;

  for (const [airportCode, airportSlots] of Object.entries(slots)) {
    if (!Array.isArray(airportSlots)) continue;

    for (const slot of airportSlots) {
      // Skip slots without departure times (old format from Supabase)
      if (!slot.departureTime) continue;

      // Skip unassigned or already departed/completed slots
      if (!slot.assignedFlightId && slot.status === SLOT_STATUS.AVAILABLE) continue;
      if (slot.status === SLOT_STATUS.DEPARTED || slot.status === SLOT_STATUS.COMPLETED) continue;

      const slotMinutes = slotTimeToMinutes(slot.departureTime);
      const minutesUntil = slotMinutes - currentTotalMinutes;

      // 15-minute warning
      if (minutesUntil <= 15 && minutesUntil > 5 && slot.status === SLOT_STATUS.ASSIGNED) {
        updates.push({ slotId: slot.slotId, airportCode, status: SLOT_STATUS.WARNING });
        warnings.push({
          type: 'warning',
          slotId: slot.slotId,
          airport: airportCode,
          minutesUntil,
          message: `Flight at ${formatTimeDisplay(slot.departureTime)} departs in ${minutesUntil} minutes - BEGIN PREFLIGHT`,
        });
      }

      // 5-minute urgent warning
      if (minutesUntil <= 5 && minutesUntil > 0 && slot.status === SLOT_STATUS.WARNING) {
        updates.push({ slotId: slot.slotId, airportCode, status: SLOT_STATUS.URGENT });
        warnings.push({
          type: 'urgent',
          slotId: slot.slotId,
          airport: airportCode,
          minutesUntil,
          message: `URGENT: ${formatTimeDisplay(slot.departureTime)} departs in ${minutesUntil} minutes - PREFLIGHT NOW`,
        });
      }

      // Slot time passed - check if should be delayed
      if (minutesUntil <= 0 && slot.status === SLOT_STATUS.URGENT) {
        // Check if flight has completed preflight
        const flight = flights?.find(f => f.id === slot.assignedFlightId);
        const preflightComplete = flight?.preflightStatus === 'READY' || flight?.preflightComplete;

        if (!preflightComplete) {
          updates.push({ slotId: slot.slotId, airportCode, status: SLOT_STATUS.DELAYED });
          warnings.push({
            type: 'delayed',
            slotId: slot.slotId,
            airport: airportCode,
            minutesUntil,
            message: `DELAYED: ${formatTimeDisplay(slot.departureTime)} missed departure - penalties accruing`,
          });
        }
      }
    }
  }

  return { updates, warnings };
}

/**
 * Apply slot status updates
 */
export function applySlotUpdates(slots, updates) {
  if (!updates || updates.length === 0) return slots;

  const newSlots = { ...slots };

  for (const update of updates) {
    const { slotId, airportCode, status } = update;
    newSlots[airportCode] = newSlots[airportCode].map(slot =>
      slot.slotId === slotId ? { ...slot, status } : slot
    );
  }

  return newSlots;
}

// ============================================================================
// DELAY PENALTY SYSTEM
// ============================================================================

/**
 * Calculate delay penalties for a late departure
 * @param {number} delayMinutes - Minutes past scheduled departure
 * @param {number} passengers - Number of passengers on flight
 * @returns {object} Penalty breakdown
 */
export function calculateDelayPenalties(delayMinutes, passengers = 76) {
  if (delayMinutes <= 0) return { total: 0, gateFees: 0, crewOvertime: 0, paxComp: 0, reputationHit: 0 };

  // Gate fees: $150/minute (airport charges for blocking gate)
  const gateFees = delayMinutes * 150;

  // Crew overtime: $50/minute (FAR 117 implications)
  const crewOvertime = delayMinutes * 50;

  // Passenger compensation (starts at 30+ min delay)
  let paxComp = 0;
  if (delayMinutes >= 120) {
    paxComp = passengers * 200;  // $200/pax for 2+ hour delay
  } else if (delayMinutes >= 60) {
    paxComp = passengers * 100;  // $100/pax for 1+ hour delay
  } else if (delayMinutes >= 30) {
    paxComp = passengers * 50;   // $50/pax for 30+ min delay
  }

  // Reputation hit: -1 point per 10 minutes delay
  const reputationHit = Math.floor(delayMinutes / 10);

  return {
    total: gateFees + crewOvertime + paxComp,
    gateFees,
    crewOvertime,
    paxComp,
    reputationHit,
    isOnTime: false,
  };
}

/**
 * Calculate current delay for a slot
 * @param {object} slot - Slot object
 * @param {Date} gameTime - Current game time
 * @returns {number} Delay in minutes (0 if not delayed)
 */
export function calculateSlotDelay(slot, gameTime) {
  if (slot.status !== SLOT_STATUS.DELAYED) return 0;
  if (!slot.departureTime) return 0;

  const slotMinutes = slotTimeToMinutes(slot.departureTime);
  const currentMinutes = gameTime.getHours() * 60 + gameTime.getMinutes();

  return Math.max(0, currentMinutes - slotMinutes);
}

// ============================================================================
// CASCADE DETECTION
// ============================================================================

/**
 * Check if a delayed flight will cause cascade effects
 * @param {object} slot - The delayed slot
 * @param {Array} flights - All scheduled flights
 * @param {object} slots - All slots
 * @param {number} delayMinutes - Current delay
 * @returns {Array} Array of at-risk slots
 */
export function detectCascade(slot, flights, slots, delayMinutes) {
  const atRiskSlots = [];

  if (!slot.assignedAircraftId || !slot.assignedDestination) return atRiskSlots;

  // Estimate arrival time at destination
  // Assuming average flight time of 60 minutes for regional routes
  const estimatedFlightTime = 60; // TODO: Calculate from actual route
  const slotMinutes = slotTimeToMinutes(slot.departureTime);
  const estimatedArrival = slotMinutes + delayMinutes + estimatedFlightTime;

  // Check if this aircraft has another slot at the destination
  const destSlots = slots[slot.assignedDestination] || [];

  for (const destSlot of destSlots) {
    if (destSlot.assignedAircraftId === slot.assignedAircraftId) {
      const destSlotMinutes = slotTimeToMinutes(destSlot.departureTime);

      // Need at least 30 minutes turnaround
      if (estimatedArrival + 30 > destSlotMinutes) {
        atRiskSlots.push({
          slotId: destSlot.slotId,
          airport: slot.assignedDestination,
          scheduledTime: destSlot.departureTime,
          estimatedMissBy: estimatedArrival + 30 - destSlotMinutes,
        });
      }
    }
  }

  return atRiskSlots;
}

// ============================================================================
// AIRCRAFT FILTERING
// ============================================================================

/**
 * Get aircraft available at a specific airport
 * @param {Array} aircraft - All player aircraft
 * @param {string} airportCode - Airport code to filter by
 * @returns {Array} Aircraft at this airport and available
 */
export function getAircraftAtAirport(aircraft, airportCode) {
  return aircraft.filter(a =>
    a.location === airportCode &&
    (a.status === 'available' || a.status === 'ready')
  );
}

/**
 * Get aircraft that are currently in flight
 * @param {Array} aircraft - All player aircraft
 * @returns {Array} Aircraft currently in flight
 */
export function getAircraftInFlight(aircraft) {
  return aircraft.filter(a => a.status === 'in_flight' || a.status === 'flying');
}

// ============================================================================
// CREW FILTERING
// ============================================================================

/**
 * Get crews available for a slot
 * @param {Array} crews - All crews
 * @param {Date} gameTime - Current game time
 * @param {string} slotTime - Slot departure time (HH:MM)
 * @returns {Array} Available crews with availability info
 */
export function getAvailableCrews(crews, gameTime, slotTime) {
  if (!crews || crews.length === 0) return [];

  return crews.map(crew => {
    // Check if crew is already on duty or assigned
    const isOnDuty = crew.status === 'on_duty' || crew.status === 'flying';
    const isAssigned = crew.status === 'assigned';

    // Calculate duty hours if crew has been working
    let dutyHours = 0;
    if (crew.dutyStartTime) {
      const dutyStart = new Date(crew.dutyStartTime);
      dutyHours = (gameTime - dutyStart) / (1000 * 60 * 60);
    }

    // Check rest requirements (10 hours rest required)
    let hasRequiredRest = true;
    if (crew.lastDutyEnd) {
      const restHours = (gameTime - new Date(crew.lastDutyEnd)) / (1000 * 60 * 60);
      hasRequiredRest = restHours >= 10;
    }

    // FAR 117 duty time limit (10 hours)
    const remainingDuty = Math.max(0, 10 - dutyHours);

    return {
      ...crew,
      available: !isOnDuty && !isAssigned && hasRequiredRest,
      dutyHours: Math.round(dutyHours * 10) / 10,
      remainingDuty: Math.round(remainingDuty * 10) / 10,
      hasRequiredRest,
      unavailableReason: isOnDuty ? 'On duty' :
                          isAssigned ? 'Already assigned' :
                          !hasRequiredRest ? 'Needs rest' : null,
    };
  });
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get all slots sorted by departure time
 */
export function getAllSlotsSorted(slots) {
  const allSlots = [];

  for (const [airportCode, airportSlots] of Object.entries(slots)) {
    for (const slot of airportSlots) {
      allSlots.push({ ...slot, airportCode });
    }
  }

  return allSlots.sort((a, b) =>
    slotTimeToMinutes(a.departureTime) - slotTimeToMinutes(b.departureTime)
  );
}

/**
 * Get slots for a specific airport
 */
export function getAirportSlots(slots, airportCode) {
  return (slots[airportCode] || []).sort((a, b) =>
    slotTimeToMinutes(a.departureTime) - slotTimeToMinutes(b.departureTime)
  );
}

/**
 * Get airports where player has slots
 */
export function getAirportsWithSlots(slots) {
  return Object.keys(slots).filter(code => slots[code]?.length > 0);
}

/**
 * Check if current time is within warning window for a slot
 */
export function getSlotWarningLevel(slot, gameTime) {
  const slotMinutes = slotTimeToMinutes(slot.departureTime);
  const currentMinutes = gameTime.getHours() * 60 + gameTime.getMinutes();
  const minutesUntil = slotMinutes - currentMinutes;

  if (minutesUntil < 0) return 'delayed';
  if (minutesUntil <= 5) return 'urgent';
  if (minutesUntil <= 15) return 'warning';
  return 'normal';
}

// ============================================================================
// FLIGHT ESTIMATION
// ============================================================================

/**
 * Calculate distance between two airports in nautical miles
 */
function calculateDistance(origin, destination) {
  const originAirport = AIRPORTS[origin];
  const destAirport = AIRPORTS[destination];

  if (!originAirport?.lat || !destAirport?.lat) return 200; // Default 200nm

  const lat1 = originAirport.lat * Math.PI / 180;
  const lat2 = destAirport.lat * Math.PI / 180;
  const dLat = (destAirport.lat - originAirport.lat) * Math.PI / 180;
  const dLon = (destAirport.lon - originAirport.lon) * Math.PI / 180;

  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  const earthRadiusNM = 3440.065; // nautical miles
  return earthRadiusNM * c;
}

/**
 * Get estimated flight duration in minutes
 * @param {string} origin - Origin airport code
 * @param {string} destination - Destination airport code
 * @returns {number} Estimated flight duration in minutes
 */
export function getFlightDuration(origin, destination) {
  const distance = calculateDistance(origin, destination);

  // Average regional jet speed: 400 knots
  // Add 15 minutes for taxi, takeoff, and landing
  const cruiseSpeed = 400;
  const overheadMinutes = 15;

  const flightTime = (distance / cruiseSpeed) * 60;
  return Math.round(flightTime + overheadMinutes);
}

/**
 * Estimate revenue for a flight
 * @param {string} origin - Origin airport code
 * @param {string} destination - Destination airport code
 * @param {number} capacity - Aircraft capacity (passengers)
 * @returns {number} Estimated revenue in dollars
 */
export function estimateRevenue(origin, destination, capacity = 76) {
  const distance = calculateDistance(origin, destination);

  // Base fare calculation
  // $0.15 per passenger-mile, 75% load factor
  const loadFactor = 0.75;
  const pricePerMile = 0.15;
  const passengers = Math.floor(capacity * loadFactor);

  const baseFare = distance * pricePerMile * passengers;

  // Add premium for popular routes
  const originAirport = AIRPORTS[origin];
  const destAirport = AIRPORTS[destination];

  let multiplier = 1.0;
  if (originAirport?.tier === 1 || destAirport?.tier === 1) multiplier += 0.2;
  if (originAirport?.tier === 2 || destAirport?.tier === 2) multiplier += 0.1;

  return Math.round(baseFare * multiplier);
}
